<template>
    <div id="form">
        <div
            ref="formJsElement"
            id="form-js"
            :class="{ 'editing': isEditing }"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, markRaw } from "vue";
import { Form, FormEditor } from "@bpmn-io/form-js";
import { exists } from "@/utils";
import baseForm from "@/assets/base.form?raw";
import { Artefact } from "@/lib/artefacts";

const model = defineModel<Artefact>({ required: true });

const props = defineProps<{
    isEditing: boolean;
}>();

const emit = defineEmits<{
    (e: "updating", value: boolean): void;
    (e: "changed"): void;
    (e: "element-selected"): void;
}>();

const formJsElement = ref<HTMLElement>();
const formJs = ref(
    markRaw({} as InstanceType<typeof FormEditor | typeof Form>)
);

watch(
    () => props.isEditing,
    (newValue, oldValue) => {
        if (newValue !== oldValue) {
            initFormJs();
        }
    }
);

onMounted(() => {
    initFormJs();
});

onUnmounted(() => {
    formJs.value?.destroy();
});

async function initFormJs() {
    if (exists(formJs.value)) {
        formJs.value.detach();
    }
    if (props.isEditing) {
        formJs.value = markRaw(new FormEditor({}));
    } else {
        formJs.value = markRaw(new Form({}));
    }
    formJs.value.attachTo(formJsElement.value);
    await loadSchema();
    addFormJsEventListeners();
}

function addFormJsEventListeners() {
    if (props.isEditing) {
        formJs.value.on("commandStack.changed", () => {
            emit("updating", true);
            emit("changed");
            const schema = (formJs.value as FormEditor).saveSchema();
            model.value.content = JSON.stringify(schema);
            emit("updating", false);
        });
    }
}

async function loadSchema() {
    const schema = JSON.parse(model.value.content || baseForm);
    try {
        await formJs.value.importSchema(schema);
    } catch (err) {
        console.warn(err.message, err.warnings);
    }
}
</script>

<style lang="sass" scoped>
@import url("https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:ital,wght@0,400;0,600;1,400&display=swap")
:deep(#object-diagram-modeler)
    &.editing
        border: 2px solid var(--stekz-biz-darkest-color)
    &:not(.editing) .djs-element
        /* Selected task (rect), gateway (polygon) or start/end/error (circle) */
        &.selected
            .djs-outline
                stroke: var(--stekz-biz-darkest-color)
                stroke-width: 2px

        &.hover
            .djs-outline
                stroke: var(--stekz-biz-lightest-color)
</style>
<style src="@bpmn-io/form-js/dist/assets/form-js.css"></style>
<style src="@bpmn-io/form-js/dist/assets/form-js-editor.css"></style>
